import {reactive, computed, watch} from 'vue';

export default function ServiceProvider(data = {}) {
    // Reactive state for service provider fields
    const state = reactive({
        id: data.id || '',
        name: data.name || '',
        address: data.address || '',
        email: data.email || '',
        phoneNumber: data.phoneNumber || '',
        dispatchEnabled: data.dispatchEnabled || false,
        contactPersonName: data.contactPersonName || '',
        contactPersonPhone: data.contactPersonPhone || '',
        contactPersonEmail: data.contactPersonEmail || '',
        operatingHours: data.operatingHours || '',
        logoUrl: data.logoUrl || '',
        serviceAreas: data.serviceAreas || [],
        serviceAreaAllowance: data.serviceAreaAllowance || 5.0,
    });

    // Computed properties for validations
    const validations = {
        isNameValid: computed(() => !!state.name.trim()),
        isAddressValid: computed(() => !!state.address.trim()),
        isEmailValid: computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)),
        isPhoneNumberValid: computed(() => /^\+?[0-9\s-]{7,15}$/.test(state.phoneNumber)),
        isContactPersonNameValid: computed(() => !!state.contactPersonName.trim()),
        isContactPersonPhoneValid: computed(() => /^\+?[0-9\s-]{7,15}$/.test(state.contactPersonPhone)),
        isContactPersonEmailValid: computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.contactPersonEmail)),
        isServiceAreaAllowanceValid: computed(() => {
            const allowance = parseFloat(state.serviceAreaAllowance);
            return !isNaN(allowance) && allowance > 0 && allowance <= 10;
        }),
    };

    // Individual field validation functions
    const validateName = () => {
        if (validations.isNameValid.value) {
            $('#service-provider-name-input').removeClass('is-invalid');
            $('#service-provider-name-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-name-input').addClass('is-invalid');
            $('#service-provider-name-error').removeClass('d-none');
            return false;
        }
    };

    const validateAddress = () => {
        if (validations.isAddressValid.value) {
            $('#service-provider-address-input').removeClass('is-invalid');
            $('#service-provider-address-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-address-input').addClass('is-invalid');
            $('#service-provider-address-error').removeClass('d-none');
            return false;
        }
    };

    const validateEmail = () => {
        if (validations.isEmailValid.value) {
            $('#service-provider-email-input').removeClass('is-invalid');
            $('#service-provider-email-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-email-input').addClass('is-invalid');
            $('#service-provider-email-error').removeClass('d-none');
            return false;
        }
    };

    const validatePhoneNumber = () => {
        if (validations.isPhoneNumberValid.value) {
            $('#service-provider-phone-number-input').removeClass('is-invalid');
            $('#service-provider-phone-number-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-phone-number-input').addClass('is-invalid');
            $('#service-provider-phone-number-error').removeClass('d-none');
            return false;
        }
    };

    const validateContactPersonName = () => {
        if (validations.isContactPersonNameValid.value) {
            $('#service-provider-contact-name-input').removeClass('is-invalid');
            $('#service-provider-contact-name-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-contact-name-input').addClass('is-invalid');
            $('#service-provider-contact-name-error').removeClass('d-none');
            return false;
        }
    };

    const validateContactPersonPhone = () => {
        if (validations.isContactPersonPhoneValid.value) {
            $('#service-provider-contact-phone-input').removeClass('is-invalid');
            $('#service-provider-contact-phone-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-contact-phone-input').addClass('is-invalid');
            $('#service-provider-contact-phone-error').removeClass('d-none');
            return false;
        }
    };

    const validateContactPersonEmail = () => {
        if (validations.isContactPersonEmailValid.value) {
            $('#service-provider-contact-email-input').removeClass('is-invalid');
            $('#service-provider-contact-email-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-contact-email-input').addClass('is-invalid');
            $('#service-provider-contact-email-error').removeClass('d-none');
            return false;
        }
    };

    const validateServiceAreaAllowance = () => {
        if (validations.isServiceAreaAllowanceValid.value) {
            $('#service-provider-service-area-allowance-input').removeClass('is-invalid');
            $('#service-provider-service-area-allowance-error').addClass('d-none');
            return true;
        } else {
            $('#service-provider-service-area-allowance-input').addClass('is-invalid');
            $('#service-provider-service-area-allowance-error').removeClass('d-none');
            return false;
        }
    };

    // Set up watchers for real-time validation
    watch(() => state.name, validateName);
    watch(() => state.address, validateAddress);
    watch(() => state.email, validateEmail);
    watch(() => state.phoneNumber, validatePhoneNumber);
    watch(() => state.contactPersonName, validateContactPersonName);
    watch(() => state.contactPersonPhone, validateContactPersonPhone);
    watch(() => state.contactPersonEmail, validateContactPersonEmail);
    watch(() => state.serviceAreaAllowance, validateServiceAreaAllowance);

    // Validation logic for the entire form
    const isValid = () => {
        let isNameValid = validateName();
        let isAddressValid = validateAddress();
        let isEmailValid = validateEmail();
        let isPhoneNumberValid = validatePhoneNumber();
        let isContactPersonNameValid = validateContactPersonName();
        let isContactPersonPhoneValid = validateContactPersonPhone();
        let isContactPersonEmailValid = validateContactPersonEmail();
        let isServiceAreaAllowanceValid = validateServiceAreaAllowance();

        return isNameValid && isAddressValid && isEmailValid && isPhoneNumberValid && 
               isContactPersonNameValid && isContactPersonPhoneValid && isContactPersonEmailValid &&
               isServiceAreaAllowanceValid;
    };

    // Initialize validation
    const initializeValidation = () => {
        // Only validate fields that have values
        if (state.name) validateName();
        if (state.address) validateAddress();
        if (state.email) validateEmail();
        if (state.phoneNumber) validatePhoneNumber();
        if (state.contactPersonName) validateContactPersonName();
        if (state.contactPersonPhone) validateContactPersonPhone();
        if (state.contactPersonEmail) validateContactPersonEmail();
        if (state.serviceAreaAllowance) validateServiceAreaAllowance();
    };

    // Call initialization after a short delay to ensure DOM is ready
    setTimeout(initializeValidation, 100);

    return {
        state,
        validations,
        isValid,
        initializeValidation
    };
}
